import React, { useEffect, useState, useRef } from "react";
import * as FeatherIcon from "react-feather";
import iq_api from "../../services/iqApi";
import { useUser } from "../../context/UserContext";

const ExcelInOut = () => {
  const { user } = useUser();
  const [file, setFile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);
  const userId = user?.user_id;

  const clearSelectedFile = () => {
    setFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // -----------------------------
  // Fetch List
  // -----------------------------
  const loadList = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await iq_api.get(`/gridiq/list?user_id=${userId}`);
      setJobs(res.data?.jobs || []);
    } catch (err) {
      console.error("List API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load + auto refresh every 30s
  useEffect(() => {
    loadList();

    const interval = setInterval(() => {
      loadList();
    }, 30000);

    return () => clearInterval(interval);
  }, [userId]);

  // -----------------------------
  // Upload File
  // -----------------------------
  const handleUpload = async () => {
    if (!file) return alert("Please select a file!");

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_id", userId);
      formData.append("mode", "auto");
      formData.append("add_audit_sheets", "true");
      formData.append("confidence_threshold", "0.5");

      await iq_api.post("/gridiq/run", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      clearSelectedFile();
      setShowModal(false);

      await loadList();
    } catch (err) {
      console.error("Upload Error:", err);
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // -----------------------------
  // Download File
  // -----------------------------
  const handleDownload = async (workbookId) => {
    try {
      const res = await iq_api.get(`/gridiq/${workbookId}/download`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `workbook_${workbookId}.xlsx`;
      link.click();
    } catch (err) {
      console.error("Download Error:", err);
      alert("Download failed");
    }
  };

  // -----------------------------
  // STATIC EXCEPTION FILE DATA
  // -----------------------------
  const staticFile = {
    file_name: "Sample.xlsx",
    url: "/excel_in_excel_out/Sample.xlsx",
  };
  const staticExceptionFile = {
    workbook_id: "static-exception-file",
    file_name: "Sample.xlsx",
    status: "completed",
    mode: "static",
    created_at: "-",
    completed_at: "-",
    workbook_intent: "Exception File",
    download_url: "/excel_in_excel_out/Sample.xlsx",
  };

  const handleStaticDownload = () => {
    const link = document.createElement("a");
    link.href = staticExceptionFile.download_url;
    link.download = staticExceptionFile.file_name;
    link.click();
  };

  return (
    <>
      <div className="conatiner-fluid">
        <div className="row">
          <div
            className="all_acccounts_wrapper"
            style={{
              backgroundColor: "rgb(255, 255, 255)",
              padding: "20px",
              maxWidth: "99%",
              borderRadius: "12px",
              minHeight: "100vh",
            }}
          >
            <div className="all_accounts_block">
              {/* ------------------ Upload Modal ------------------ */}
              <div
                className={`modal fade ${showModal ? "show d-block" : ""}`}
                tabIndex="-1"
              >
                <div className="modal-dialog modal-md">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Upload Excel File</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowModal(false)}
                      ></button>
                    </div>

                    <div className="modal-body">
                      <label className="form-label fw-semibold">
                        Choose Excel File
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xls,.xlsx"
                        className="form-control mb-3"
                        onChange={(e) => setFile(e.target.files[0])}
                      />
                      {/* <input
                        type="file"
                        accept=".xls,.xlsx"
                        className="form-control mb-3"
                        onChange={(e) => setFile(e.target.files[0])}
                      /> */}

                      {/* ---------------- SAMPLE FILE DOWNLOAD ---------------- */}
                      <div className="mt-3 p-2 rounded bg-light border d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">Need a sample file?</span>
                        <button
                          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = "/excel_in_excel_out/Sample.xlsx";
                            link.download = "/excel_in_excel_out/Sample.xlsx";
                            link.click();
                          }}
                        >
                          <FeatherIcon.Download size={14} />
                          Sample File
                        </button>
                      </div>
                    </div>

                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowModal(false)}
                      >
                        Close
                      </button>

                      <button
                        className="btn btn-primary btn-color"
                        style={{
                          backgroundColor: "#4E6BCA",
                          color: "#fff",
                        }}
                        disabled={uploading}
                        onClick={handleUpload}
                      >
                        {uploading ? "Uploading..." : "Upload"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {showModal && <div className="modal-backdrop fade show"></div>}

              {/* ------------------ Main UI ------------------ */}
              <div className="container-fluid p-4">
                {/* Header Row */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4>Uploaded Excel Jobs</h4>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-secondary"
                      style={{
                        padding: "5px 10px",
                        backgroundColor: "rgb(35, 36, 40)",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "rgb(255, 255, 255)",
                        border: "none",
                        borderRadius: "6px",
                      }}
                      onClick={loadList}
                    >
                      <FeatherIcon.RefreshCw size={14} /> Refresh
                    </button>

                    <button
                      className="btn btn-primary"
                      style={{
                        padding: "5px 10px",
                        backgroundColor: "rgb(35, 36, 40)",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "rgb(255, 255, 255)",
                        border: "none",
                        borderRadius: "6px",
                      }}
                      onClick={() => {
                        clearSelectedFile();
                        setShowModal(true);
                      }}
                    >
                      <FeatherIcon.Upload size={14} /> Upload Excel
                    </button>
                  </div>
                </div>

                {/* Listing Table */}
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table custom-resizable-table">
                      <thead className="table-light">
                        <tr className="custom-resizable-table-header">
                          <th>File Name</th>
                          <th>Status</th>
                          <th>Mode</th>
                          <th>Created At</th>
                          <th>Completed At</th>
                          <th>Intent</th>
                          <th>Download</th>
                        </tr>
                      </thead>

                      <tbody>
                        {/* --------------------- API JOBS --------------------- */}
                        {jobs?.length ? (
                          jobs.map((job) => {
                            let created_at = new Date(job?.created_at);
                            let completed_at = new Date(job?.completed_at);
                            return (
                              <tr className="gtr-row" key={job.workbook_id}>
                                <td className="gtr-cell">
                                  <div className="gtr-cell-content-container">
                                    <div className="gtr-cell-content">
                                      {job.file_name}
                                    </div>
                                  </div>
                                </td>

                                <td className="gtr-cell">
                                  <div className="gtr-cell-content-container">
                                    <div className="gtr-cell-content">
                                      {job.status === "completed" ? (
                                        <span className="badge bg-success">
                                          Completed
                                        </span>
                                      ) : (
                                        <span className="badge bg-warning text-dark">
                                          {job.status}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="gtr-cell">
                                  <div className="gtr-cell-content-container">
                                    <div className="gtr-cell-content">
                                      {job.mode}
                                    </div>
                                  </div>
                                </td>
                                <td className="gtr-cell">
                                  <div className="gtr-cell-content-container">
                                    <div className="gtr-cell-content">
                                      {/* {job.created_at} */}
                                      {!["", "-", undefined, null].includes(
                                        job?.created_at
                                      )
                                        ? created_at.toLocaleString("en-US", {
                                            weekday: "short",
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : "-"}
                                    </div>
                                  </div>
                                </td>
                                <td className="gtr-cell">
                                  <div className="gtr-cell-content-container">
                                    <div className="gtr-cell-content">
                                      {/* {job.completed_at || "-"} */}
                                      {!["", "-", undefined, null].includes(
                                        job?.completed_at
                                      )
                                        ? completed_at.toLocaleString("en-US", {
                                            weekday: "short",
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : "-"}
                                    </div>
                                  </div>
                                </td>
                                <td className="gtr-cell">
                                  <div className="gtr-cell-content-container">
                                    <div className="gtr-cell-content">
                                      {job.workbook_intent || "-"}
                                    </div>
                                  </div>
                                </td>
                                <td className="gtr-cell">
                                  <div className="gtr-cell-content-container">
                                    <div className="gtr-cell-content">
                                      {job.status === "completed" ? (
                                        <button
                                          className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                          style={{
                                            padding: "5px 10px",
                                            backgroundColor: "rgb(35, 36, 40)",
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            color: "rgb(255, 255, 255)",
                                            border: "none",
                                            borderRadius: "6px",
                                          }}
                                          onClick={() =>
                                            handleDownload(job.workbook_id)
                                          }
                                        >
                                          <FeatherIcon.Download size={14} />{" "}
                                          Download
                                        </button>
                                      ) : (
                                        <span className="text-muted">
                                          Processing...
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr className="gtr-row text-center">
                            <td colspan="7">No sheets uploaded yet</td>
                          </tr>
                        )}

                        {/* --------------------- STATIC EXCEPTION FILE --------------------- */}
                        {/* <tr style={{ background: "#f8f9fa" }}>
                  <td>{staticExceptionFile.file_name}</td>
                  <td>
                    <span className="badge bg-info text-dark">Completed</span>
                  </td>
                  <td>{staticExceptionFile.mode}</td>
                  <td>-</td>
                  <td>-</td>
                  <td>{staticExceptionFile.workbook_intent}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary d-flex align-items-center gap-1"
                      onClick={handleStaticDownload}
                    >
                      <FeatherIcon.Download size={14} /> Download
                    </button>
                  </td>
                </tr> */}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExcelInOut;
